import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { RequestPriority } from '@kfolks/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { type ApprovalDTO, requestCenterApi } from '@/services/request-center-api'
import { useDashboard } from '../useDashboard'

const PRIORITY_RANK: Record<string, number> = {
	[RequestPriority.CRITICAL]: 0,
	[RequestPriority.HIGH]: 1,
	[RequestPriority.NORMAL]: 2,
}

/**
 * "Pending Approvals" widget — visible when the caller is admin OR has
 * pending approvals OR has ever been an approver (sticky predicate).
 *
 * Reads from the shared `useDashboard()` cache (no own fetch). Sorts
 * client-side by `due_at ASC` then by priority DESC (CRITICAL → HIGH →
 * NORMAL) within ties — the backend default is `due_at ASC` only;
 * priority is the in-page tiebreaker.
 *
 * Inline Approve / Reject buttons reuse the existing
 * `requestCenterApi.approveApproval` + `rejectApproval` endpoints; on
 * success the dashboard's pending-approvals query is invalidated so
 * the row drops out without a manual refresh.
 *
 * Spec 4 / Group 9e — `request-center-dashboard.md`.
 */
export function PendingApprovalsWidget() {
	const { pendingApprovalsVisible, pendingApprovalsQuery } = useDashboard()

	if (!pendingApprovalsVisible) return null

	if (pendingApprovalsQuery.isError) {
		return (
			<WidgetCard title="Pending Approvals">
				<p className="text-sm text-red-700">Couldn't load pending approvals. Try refreshing the page.</p>
			</WidgetCard>
		)
	}

	if (pendingApprovalsQuery.isLoading || pendingApprovalsQuery.isPending) {
		return (
			<WidgetCard title="Pending Approvals">
				<p className="text-sm text-gray-500">Loading…</p>
			</WidgetCard>
		)
	}

	const rows = (pendingApprovalsQuery.data?.data ?? []).slice()
	rows.sort(compareByDueThenPriority)

	if (rows.length === 0) {
		return (
			<WidgetCard title="Pending Approvals">
				<p className="text-sm text-gray-500">No pending approvals right now.</p>
			</WidgetCard>
		)
	}

	return (
		<WidgetCard title="Pending Approvals">
			<ul className="divide-y divide-gray-100" aria-label="Pending approvals list">
				{rows.map((row) => (
					<ApprovalRow key={row.id} row={row} />
				))}
			</ul>
		</WidgetCard>
	)
}

/**
 * Sort helper exposed for testability + reuse by the
 * `UpcomingSlaWidget` (which sorts the same set after filtering).
 */
export function compareByDueThenPriority(a: ApprovalDTO, b: ApprovalDTO): number {
	// 1. Primary: ascending dueAt (rows with no dueAt sink to the bottom).
	const aDue = a.dueAt ? new Date(a.dueAt).getTime() : Number.POSITIVE_INFINITY
	const bDue = b.dueAt ? new Date(b.dueAt).getTime() : Number.POSITIVE_INFINITY
	if (aDue !== bDue) return aDue - bDue
	// 2. Tiebreaker: priority DESC — CRITICAL (rank 0) first, NORMAL (2) last.
	const aRank = PRIORITY_RANK[a.request.priority] ?? 99
	const bRank = PRIORITY_RANK[b.request.priority] ?? 99
	return aRank - bRank
}

/**
 * Inline Approve / Reject button pair. Extracted so `ApprovalRow` stays
 * under the project's `max-lines-per-function` lint cap.
 */
function ApprovalActions({ approvalId }: { approvalId: string }) {
	const queryClient = useQueryClient()
	const [busy, setBusy] = useState<'approve' | 'reject' | null>(null)

	const handleAction = async (action: 'approve' | 'reject') => {
		if (busy) return
		setBusy(action)
		try {
			if (action === 'approve') {
				await requestCenterApi.approveApproval(approvalId)
			} else {
				// Inline reject from the dashboard skips the comment dialog.
				// Submit a placeholder — full rejection flow with reason is
				// on the dedicated approval-detail page.
				await requestCenterApi.rejectApproval(approvalId, 'Rejected from dashboard')
			}
			await queryClient.invalidateQueries({ queryKey: ['dashboard', 'pending-approvals'] })
			await queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats', 'my'] })
		} catch {
			// Swallow — ApprovalsInbox is the canonical place for detailed
			// error surfacing; dashboard inline buttons are best-effort.
		} finally {
			setBusy(null)
		}
	}

	return (
		<div className="flex items-center gap-2">
			<Button
				size="sm"
				variant="default"
				onClick={() => {
					void handleAction('approve')
				}}
				disabled={busy !== null}
			>
				Approve
			</Button>
			<Button
				size="sm"
				variant="outline"
				onClick={() => {
					void handleAction('reject')
				}}
				disabled={busy !== null}
			>
				Reject
			</Button>
		</div>
	)
}

function ApprovalRow({ row }: { row: ApprovalDTO }) {
	const dueLabel = row.dueAt ? `due ${new Date(row.dueAt).toLocaleDateString()}` : null

	return (
		<li className="px-2 py-3">
			<div className="flex items-center justify-between gap-3">
				<div className="space-y-0.5">
					<div className="flex items-center gap-2">
						<Link
							to={`/requests-center/approvals/${row.id}`}
							className="font-mono text-sm font-medium text-gray-900 hover:underline"
						>
							{row.request.requestNumber}
						</Link>
						<PriorityBadge priority={row.request.priority} />
					</div>
					<p className="text-xs text-gray-500">
						{row.stepLabel ? `${row.stepLabel} • ` : ''}
						{dueLabel ?? 'no due date'}
					</p>
				</div>
				<ApprovalActions approvalId={row.id} />
			</div>
		</li>
	)
}

function PriorityBadge({ priority }: { priority: string }) {
	if (priority === RequestPriority.CRITICAL)
		return (
			<Badge variant="outline" className="border-red-300 bg-red-50 text-red-700">
				Critical
			</Badge>
		)
	if (priority === RequestPriority.HIGH)
		return (
			<Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
				High
			</Badge>
		)
	return <Badge variant="outline">Normal</Badge>
}

function WidgetCard({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<Card className="p-4">
			<h2 className="mb-3 text-sm font-semibold text-gray-700">{title}</h2>
			{children}
		</Card>
	)
}
